import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Sparkle } from "lucide-react";
import Footer from "@/components/Footer";
import { API } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];
const EMAIL_RE = /^\S+@\S+\.\S+$/;

const SESSION_TYPES = [
  { value: "60_min", name: "The Full Session", meta: "60 minutes — $150", note: "A private conversation offering space to explore the questions, patterns, transitions, and spiritual matters currently present in your life.", testId: "session-type-60" },
  { value: "30_min", name: "The Focused Session", meta: "30 minutes — $85", note: "For people carrying one focused question that deserves a clear, undivided light.", testId: "session-type-30" },
];

const DIVINATION_OPTIONS = [
  { value: "tarot_oracle", label: "Tarot/oracle cards", testId: "session-divination-tarot-oracle" },
  { value: "astrology", label: "Astrology", testId: "session-divination-astrology" },
  { value: "both", label: "Both", testId: "session-divination-both" },
  { value: "conversation_only", label: "Prefer conversation only", testId: "session-divination-conversation-only" },
];

const INITIAL = {
  session_type: "60_min",
  full_name: "",
  preferred_name: "",
  email: "",
  birth_date: "",
  birth_time: "",
  birthplace: "",
  explore: "",
  questions: "",
  divination: "",
  notes: "",
  disclaimer_ack: false,
  outcome_ack: false,
};

const inputCls =
  "w-full border-b border-[#D49A36]/25 bg-transparent pb-2.5 text-base text-[#F2EFE9] outline-none transition-colors duration-300 placeholder:text-[#DCD8D3]/30 focus:border-[#D49A36]";

const Field = ({ label, hint, children }) => (
  <div>
    <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#D49A36]/80">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-[#DCD8D3]/50">{hint}</span>}
  </div>
);

export default function SessionsPage() {
  const lenis = useLenis();
  const formRef = useRef(null);
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
  }, [lenis]);

  const choose = (type) => {
    setForm((f) => ({ ...f, session_type: type }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "preferred_name", "birth_date", "birth_time", "birthplace", "explore"];
    if (required.some((key) => !form[key].trim())) {
      toast.error("A few fields are still empty — everything but the optional notes is needed.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error("That email doesn't look quite right — try again.");
      return;
    }
    if (!form.divination) {
      toast.error("Let Umi know how you feel about divination tools.");
      return;
    }
    if (!form.disclaimer_ack || !form.outcome_ack) {
      toast.error("Please read the acknowledgment and check both boxes before booking.");
      return;
    }
    setLoading(true);
    try {
      const { session_type, full_name, preferred_name, email, birth_date, birth_time, birthplace, explore, questions, divination, notes, disclaimer_ack, outcome_ack } = form;
      await axios.post(`${API}/session-request`, {
        session_type, full_name, preferred_name, email: email.trim(), birth_date, birth_time,
        birthplace, explore, questions, divination, notes, disclaimer_ack, outcome_ack,
      });
      toast.success("Your request is on its way. Watch your inbox for a confirmation.");
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong on our end — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="sessions-page" className="bg-[#131110] text-[#F2EFE9]">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-[#D49A36]/10 bg-[#131110]/70 backdrop-blur-md">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" data-testid="sessions-nav-logo" className="flex items-center gap-2 font-display text-xl italic text-[#F2EFE9] transition-colors duration-300 hover:text-[#D49A36]">
            <Sparkle className="h-4 w-4 text-[#D49A36]" strokeWidth={1.5} />
            Umi Speaks
          </Link>
          <Link to="/" data-testid="sessions-back-home" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#DCD8D3] transition-colors duration-300 hover:text-[#D49A36] md:text-xs">
            Back to the site
          </Link>
        </nav>
      </header>

      <section className="px-6 pb-16 pt-36 md:px-12 md:pt-44">
        <div className="mx-auto max-w-7xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D49A36] md:text-xs"
          >
            1:1 Virtual Sessions
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="max-w-3xl font-display text-5xl font-medium leading-[1.0] tracking-tight text-[#F2EFE9] md:text-7xl"
          >
            A private hour, <span className="italic text-[#D49A36]">held with care.</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
            className="mt-10 max-w-2xl space-y-5 text-base leading-relaxed text-[#DCD8D3] md:text-lg"
          >
            <p>
              Our time together is intuitive and conversational rather than formulaic. Depending on what arises,
              I may incorporate tarot or oracle cards, astrology, spiritual reflection, and other divination
              tools to help illuminate patterns, possibilities, and areas deserving deeper consideration.
            </p>
            <p>
              You are welcome to arrive with a specific question or simply with an area of life you'd like to
              explore. These sessions are intended to support reflection, discernment, personal responsibility,
              and a deeper relationship with your own inner knowing — not to make your decisions for you or
              promise a particular outcome.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          {SESSION_TYPES.map((offering, i) => (
            <motion.div
              key={offering.value}
              data-testid={`offering-card-${offering.value}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.12 * i, ease: EASE }}
              className={`flex flex-col border p-8 transition-colors duration-500 md:p-10 ${
                form.session_type === offering.value ? "border-[#D49A36]/60 bg-[#1E1A18]" : "border-[#D49A36]/15 bg-[#1E1A18] hover:border-[#D49A36]/40"
              }`}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">{offering.meta}</p>
              <h2 className="mt-3 font-display text-3xl font-medium text-[#F2EFE9] md:text-4xl">{offering.name}</h2>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-[#DCD8D3]/85 md:text-base">{offering.note}</p>
              <button
                data-testid={offering.testId}
                onClick={() => choose(offering.value)}
                className={`mt-8 self-start px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] transition-colors duration-300 ${
                  form.session_type === offering.value
                    ? "bg-[#D49A36] text-[#131110]"
                    : "border border-[#D49A36]/50 text-[#D49A36] hover:bg-[#D49A36] hover:text-[#131110]"
                }`}
              >
                {form.session_type === offering.value ? "Chosen" : "Choose this session"}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <section ref={formRef} className="scroll-mt-24 px-6 pb-32 md:px-12">
        <motion.form
          data-testid="session-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mx-auto max-w-3xl space-y-8 border border-[#D49A36]/15 bg-[#1E1A18] p-7 md:p-12"
        >
          <div>
            <p className="font-display text-2xl italic text-[#F2EFE9] md:text-3xl">The Questionnaire</p>
            <p className="mt-2 text-sm text-[#DCD8D3]/70">
              Please complete this brief questionnaire when booking so I can become familiar with what you'd
              like to explore before we meet.
            </p>
            <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">
              Requesting: {SESSION_TYPES.find((t) => t.value === form.session_type)?.name} — {SESSION_TYPES.find((t) => t.value === form.session_type)?.meta}
            </p>
          </div>

          <Field label="Full name">
            <input data-testid="session-full-name-input" value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Your full name" />
          </Field>

          <Field label="What name would you like me to call you during our session?">
            <input data-testid="session-preferred-name-input" value={form.preferred_name} onChange={set("preferred_name")} className={inputCls} placeholder="The name that feels like you" />
          </Field>

          <Field label="Email address">
            <input data-testid="session-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
          </Field>

          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="Date of birth">
              <input data-testid="session-birth-date-input" value={form.birth_date} onChange={set("birth_date")} className={inputCls} placeholder="MM/DD/YYYY" />
            </Field>
            <Field label="Exact birth time, if known" hint='If unknown, simply write "unknown."'>
              <input data-testid="session-birth-time-input" value={form.birth_time} onChange={set("birth_time")} className={inputCls} placeholder="e.g. 6:42 AM" />
            </Field>
          </div>

          <Field label="Birthplace — city, state/province, country">
            <input data-testid="session-birthplace-input" value={form.birthplace} onChange={set("birthplace")} className={inputCls} placeholder="Jackson, Mississippi, USA" />
          </Field>

          <Field label="What would you most like to explore during our time together?" hint="Please keep your response to approximately 3–5 sentences.">
            <textarea data-testid="session-explore-input" value={form.explore} onChange={set("explore")} rows={4} className={`${inputCls} resize-none`} placeholder="Speak freely…" />
          </Field>

          <Field label="Are there any specific questions you'd like to bring into the session?" hint="You may list up to three.">
            <textarea data-testid="session-questions-input" value={form.questions} onChange={set("questions")} rows={3} className={`${inputCls} resize-none`} placeholder="Optional" />
          </Field>

          <Field label="Are you comfortable with divination tools being incorporated into your session?">
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {DIVINATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  data-testid={option.testId}
                  onClick={() => setForm((f) => ({ ...f, divination: option.value }))}
                  className={`border px-4 py-3 text-left text-sm transition-colors duration-300 ${
                    form.divination === option.value
                      ? "border-[#D49A36] bg-[#D49A36]/10 text-[#D49A36]"
                      : "border-[#D49A36]/20 text-[#DCD8D3] hover:border-[#D49A36]/50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Is there anything important you'd like me to know before we meet?" hint="Optional; 2–3 sentences maximum.">
            <textarea data-testid="session-notes-input" value={form.notes} onChange={set("notes")} rows={2} className={`${inputCls} resize-none`} placeholder="Optional" />
          </Field>

          <div data-testid="disclaimer-box" className="border border-[#D49A36]/20 bg-[#131110] p-6">
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">Please acknowledge before booking</p>
            <p data-testid="disclaimer-text" className="text-sm leading-relaxed text-[#DCD8D3]/80">
              I understand that Umi Speaks 1:1 sessions are intended for spiritual exploration, personal
              reflection, and educational purposes. Divination and astrology are interpretive practices and do
              not guarantee outcomes or predict events with certainty. Sessions are not a substitute for medical
              or mental-health care, legal counsel, financial advice, or other licensed professional services. I
              understand that I remain responsible for my own decisions and will seek an appropriately qualified
              professional regarding medical, psychological, legal, financial, or other matters requiring
              professional care.
            </p>
            <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-[#F2EFE9]">
              <input
                type="checkbox"
                data-testid="disclaimer-checkbox"
                checked={form.disclaimer_ack}
                onChange={(e) => setForm((f) => ({ ...f, disclaimer_ack: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#D49A36]"
              />
              I have read and understand the above.
            </label>
            <label className="mt-3 flex cursor-pointer items-start gap-3 text-sm text-[#F2EFE9]">
              <input
                type="checkbox"
                data-testid="outcome-checkbox"
                checked={form.outcome_ack}
                onChange={(e) => setForm((f) => ({ ...f, outcome_ack: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#D49A36]"
              />
              I understand that purchasing a session does not guarantee a particular outcome, answer,
              prediction, or spiritual result.
            </label>
          </div>

          <button
            data-testid="session-submit-button"
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-3 bg-[#D49A36] px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#131110] transition-colors duration-300 hover:bg-[#F2EFE9] disabled:opacity-60"
          >
            {loading ? "Sending…" : "Request a session"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </button>
        </motion.form>
      </section>

      <Footer />
    </main>
  );
}
